import { useEffect, useState } from 'react'
import { invoiceEmailRecipients, type TmsInvoiceRow } from '@numeo/types-main-web-app'
import invoiceApi from '@/services/invoiceApi'
import { ongoingLoadsApi } from '@/pages/dashboard/pages/updater-agent/pages/ongoing-loads/api/ongoing-loads.api'
import { rowKeyOf } from './invoicingRows'

/** One row's billing address as the send will use it: `undefined` while it is read, '' when none is on file. */
type BillingEmails = Record<string, string | undefined>

/**
 * The address each invoice email goes to, read before the click so the user approves the address they see: the Invoices page dialog's
 * rule (`invoiceEmailRecipients`), the one the send card applies. An invoice row reads its invoice with its loads; a load with no invoice
 * yet reads the load, whose rate con's accounting email the new invoice will carry. A failed read answers '' (the user types one).
 */
export const useBillingEmails = (rows: TmsInvoiceRow[], open: boolean): BillingEmails => {
    const [emails, setEmails] = useState<BillingEmails>({})
    const keys = rows.map(rowKeyOf).join('|')
    useEffect(() => {
        if (!open) return
        let live = true
        const readOne = async (row: TmsInvoiceRow): Promise<string> => {
            if (row.invoice_id) {
                const read = await invoiceApi.getBrokerInvoice(row.invoice_id)
                return read.success && read.data ? (invoiceEmailRecipients([read.data])[0] ?? '') : ''
            }
            if (!row.load_id) return ''
            const load = await ongoingLoadsApi.getOngoingLoadById(row.load_id)
            return invoiceEmailRecipients([{ loads: [{ fullLoadDetails: { invoicingEmail: load?.invoicingEmail } }] }])[0] ?? ''
        }
        for (const row of rows) {
            const key = rowKeyOf(row)
            readOne(row)
                .catch(() => '')
                .then((email) => {
                    if (live) setEmails((current) => ({ ...current, [key]: email }))
                })
        }
        return () => {
            live = false
        }
        // The rows are read again only when the drawer opens on another selection.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, keys])
    return emails
}
